import {
    CallActionResponseData,
    EmptyCb,
    FrameMessageV1,
    FrameMessageV2,
    FrameMessageAbort,
    FrameMessageAbortReason,
    FrameMessageAppleToken,
    FrameMessageGoogleToken,
    FrameMessageCallActionResponse,
    FrameMessageDiscount,
    FrameMessageDiscountData,
    FrameMessageError,
    FrameMessageFormattedPaymentMethod,
    FrameMessageFormInit,
    FrameMessageListener,
    FrameMessagePaymentInfo,
    FrameMessagePaymentLoaded,
    FrameMessageExternal3ds,
    FrameMessageReload,
    FrameMessageResize,
    FrameMessageResizeSize,
    FrameMessageSuccess,
    FrameMessageType,
    PaymentMessage,
    External3dsData,
    FrameMessageTdsSuccessStatus,
    FrameMessageEscPressed,
    FrameMessageTokenizeComplete,
    FrameMessageTokenizeFail,
    PaymentMethodDescriptors,
    FrameMessageRequestOpenUrl,
} from '../../types';
import { checkIsEventCanBeDoubled } from '../../utils/common';
import { getPaymentMessage } from '../../utils/message';
import { normalizeCardId } from '../../utils/normalizeCardId';
import { normalizeSbpId } from '../../utils/normalizeSbpId';
import { normalizeWalletId } from '../../utils/normalizeWalletId';

// Из фрейма события дублируются в виде JSON и сериализованной строки
function getDuplicateMessageChecker() {
    let lastMessage = '';
    const toPlain = (msg: PaymentMessage) => `${msg.source}:${msg.type}:${JSON.stringify(msg.data)}`;

    return (msg: PaymentMessage) => {
        const message = toPlain(msg);
        const isSame = lastMessage === message;

        lastMessage = message;

        return isSame;
    };
}

function createChainMessageHandler(
    rules: Array<(message: PaymentMessage) => boolean>,
    onMatch: (...messages: PaymentMessage[]) => void,
): (message: PaymentMessage) => void {
    let stack: PaymentMessage[] = [];

    return (message: PaymentMessage) => {
        if (rules[stack.length]!(message)) {
            stack.push(message);

            if (stack.length === rules.length) {
                onMatch(...stack);
                stack = [];
            }
        }
    };
}

function createLoadedMessage(): FrameMessagePaymentLoaded {
    return {
        type: FrameMessageType.paymentLoaded,
    };
}

function createFormInitMessage(): FrameMessageFormInit {
    return {
        type: FrameMessageType.formInit,
    };
}

const isCardFormattedPaymentMethod = (
    paymentMethod: FrameMessageFormattedPaymentMethod | FrameMessagePaymentInfo,
): paymentMethod is FrameMessageFormattedPaymentMethod => {
    return (
        (paymentMethod as FrameMessageFormattedPaymentMethod).type === 'card' &&
        Boolean((paymentMethod as FrameMessageFormattedPaymentMethod).id)
    );
};

export function createSuccessMessage(
    payload:
        | FrameMessagePaymentInfo
        | FrameMessageFormattedPaymentMethod
        | FrameMessageAppleToken
        | FrameMessageGoogleToken
        | FrameMessageTdsSuccessStatus,
): FrameMessageSuccess {
    if ('type' in payload && payload.type === '3ds-success') {
        return {
            type: FrameMessageType.success,
            source: FrameMessageType.tdsSuccess,
        };
    }

    if ('type' in payload && (payload.type === 'apple_token' || payload.type === 'google_token')) {
        return {
            type: FrameMessageType.success,
            tokenId: payload.tokenId,
        };
    }

    if ('type' in payload && payload.type === 'sbp_token_v2') {
        return {
            type: FrameMessageType.success,
            tokenId: normalizeSbpId(payload.id),
        };
    }

    if ('type' in payload && (payload.type === 'click_wallet' || payload.type === 'mbank_account')) {
        return {
            type: FrameMessageType.success,
            tokenId: normalizeWalletId(payload.id, payload.type),
        };
    }

    let cardId: string | undefined;

    if (isCardFormattedPaymentMethod(payload)) {
        cardId = normalizeCardId(payload.id);
    } else if (payload.card_id) {
        cardId = normalizeCardId(payload.card_id);
    }

    return {
        type: FrameMessageType.success,
        cardId,
        payment: payload,
    };
}

function createErrorMessage(reason: string): FrameMessageError {
    return {
        type: FrameMessageType.error,
        reason,
    };
}

function createAbortMessage(reason: FrameMessageAbortReason): FrameMessageAbort {
    return {
        type: FrameMessageType.abort,
        reason,
    };
}

function createResizeMessage(size: FrameMessageResizeSize): FrameMessageResize {
    return {
        type: FrameMessageType.resize,
        size: {
            width: size.width,
            height: size.height,
        },
    };
}

function createEscPressedMessage(): FrameMessageEscPressed {
    return {
        type: FrameMessageType.escPressed,
    };
}

function createDiscountMessage(data: FrameMessageDiscountData & { original_amount: string }): FrameMessageDiscount {
    return {
        type: FrameMessageType.discount,
        data: {
            amount: data.amount,
            originalAmount: data.original_amount,
            discounts: data.discounts,
            promocode: data.promocode,
        },
    };
}

function createCallActionMessage(data: CallActionResponseData): FrameMessageCallActionResponse {
    return {
        type: FrameMessageType.callActionResponse,
        data,
    };
}

function createReloadMessage(): FrameMessageReload {
    return {
        type: FrameMessageType.reload,
    };
}

function createRedirect3dsMessage(data: External3dsData): FrameMessageExternal3ds {
    return {
        type: FrameMessageType.external3ds,
        data,
    };
}

function createTokenizeCompleteMessage({
    descriptors,
}: {
    descriptors: PaymentMethodDescriptors;
}): FrameMessageTokenizeComplete {
    return {
        type: FrameMessageType.tokenizeComplete,
        descriptors,
    };
}

function createTokenizeFailMessage(): FrameMessageTokenizeFail {
    return {
        type: FrameMessageType.tokenizeFail,
    };
}

function createRequestOpenUrlMessage(url: string): FrameMessageRequestOpenUrl {
    return {
        type: FrameMessageType.requestOpenUrl,
        url,
    };
}

function createFrameMessagesHandler(
    onMessage: (message: FrameMessageV1 | FrameMessageV2) => void,
): (message: PaymentMessage) => void {
    const checkoutHandlers = [
        // die hard loaded
        createChainMessageHandler(
            [(msg) => msg.type === FrameMessageType.initCheckout, (msg) => msg.type === FrameMessageType.paymentLoaded],
            () => onMessage(createLoadedMessage()),
        ),

        // Form is initialized
        createChainMessageHandler([(msg) => msg.type === FrameMessageType.formInit], () =>
            onMessage(createFormInitMessage()),
        ),

        // Success
        createChainMessageHandler(
            [
                (msg) => msg.type === FrameMessageType.initCheckout,
                (msg) => msg.type === FrameMessageType.paymentComplete,
            ],
            (_, msg) => onMessage(createSuccessMessage(msg.data)),
        ),

        // Failure
        createChainMessageHandler(
            [(msg) => msg.type === FrameMessageType.initCheckout, (msg) => msg.type === FrameMessageType.paymentFail],
            (_, msg) => onMessage(createErrorMessage(msg.data.reason || msg.data.status)),
        ),

        // Error
        createChainMessageHandler([(msg) => msg.type === FrameMessageType.error], (msg) =>
            onMessage(createErrorMessage(msg.data.error)),
        ),

        // Abort
        createChainMessageHandler([(msg) => msg.type === FrameMessageType.paymentTimeout], () =>
            onMessage(createAbortMessage(FrameMessageAbortReason.timeout)),
        ),
        createChainMessageHandler([(msg) => msg.type === FrameMessageType.paymentCancel], () =>
            onMessage(createAbortMessage(FrameMessageAbortReason.user)),
        ),

        // Resize
        createChainMessageHandler([(msg) => msg.type === FrameMessageType.onResize], (msg) =>
            onMessage(createResizeMessage(msg.data)),
        ),

        // Discount
        createChainMessageHandler([(msg) => msg.type === FrameMessageType.paymentPreview], (msg) =>
            onMessage(createDiscountMessage(msg.data)),
        ),

        // Call Action
        createChainMessageHandler([(msg) => msg.type === FrameMessageType.callActionResponse], (msg) =>
            onMessage(createCallActionMessage(msg.data)),
        ),

        // Binding Tried Again After Error
        createChainMessageHandler([(msg) => msg.type === FrameMessageType.reload], () =>
            onMessage(createReloadMessage()),
        ),

        // Open 3ds challenge ourside payment form
        createChainMessageHandler([(msg) => msg.type === FrameMessageType.external3ds], (msg) =>
            onMessage(createRedirect3dsMessage(msg.data)),
        ),

        createChainMessageHandler([(msg) => msg.type === FrameMessageType.escPressed], () =>
            onMessage(createEscPressedMessage()),
        ),

        createChainMessageHandler([(msg) => msg.type === FrameMessageType.requestOpenUrl], (msg) => {
            onMessage(createRequestOpenUrlMessage(msg.data.url));
        }),
    ];

    const oldFormHandlers = [
        // Success
        // Важно! В случае привязки 6-ой карты (максимум 5) приходит success,
        //        но в binding_result будет binding_error
        createChainMessageHandler(
            [
                (msg) => msg.type === 'payment-status' && msg.data.value === 'success',
                (msg) => msg.type === 'payment' && msg.data.binding_result !== 'binding_error',
            ],
            (msg1, msg2) => onMessage(createSuccessMessage(msg2.data)),
        ),
        createChainMessageHandler(
            [
                (msg) => msg.type === '3ds-status' && msg.data === '3ds-success',
                (msg) => msg.type === 'payment' && msg.data.binding_result !== 'binding_error',
            ],
            (msg1, msg2) => onMessage(createSuccessMessage(msg2.data)),
        ),
        createChainMessageHandler([(msg) => msg.type === '3DS_SUCCESS'], () =>
            onMessage(createSuccessMessage({ type: FrameMessageType.tdsSuccess })),
        ),

        // Error
        createChainMessageHandler(
            [
                (msg) => msg.type === 'payment-status' && msg.data.value === 'wait_for_notification',
                // NB: В случае ошибки статус идет в msg.data.value отдельным событием перед! payment-check-failure
                (msg) => msg.type === 'payment-status' && msg.data.value !== 'wait_for_notification',
                (msg) => msg.type === 'payment-check-failure',
            ],
            (msg1, msg2) => onMessage(createErrorMessage(msg2.data.value)),
        ),
        createChainMessageHandler([(msg) => msg.type === 'payment-update-failure'], () =>
            onMessage(createErrorMessage('request_failure')),
        ),
        createChainMessageHandler([(msg) => msg.type === '3ds-status' && msg.data === '3ds-failure'], () =>
            onMessage(createErrorMessage('3ds_failure')),
        ),
        createChainMessageHandler([(msg) => msg.type === '3DS_FAIL'], () =>
            onMessage(createErrorMessage('3ds_failure')),
        ),
        // Важно! В случае привязки 6-ой карты (максимум 5) приходит success,
        //        но в binding_result будет binding_error
        createChainMessageHandler(
            [(msg) => msg.type === 'payment' && msg.data.binding_result === 'binding_error'],
            () => onMessage(createErrorMessage('binding_error')),
        ),

        // Abort
        createChainMessageHandler([(msg) => msg.type === 'payment-status' && msg.data.value === 'abort'], () =>
            onMessage(createAbortMessage(FrameMessageAbortReason.user)),
        ),

        // Tokenize-complete
        createChainMessageHandler([(msg) => msg.type === 'tokenize-complete'], (msg) =>
            onMessage(createTokenizeCompleteMessage(msg.data)),
        ),

        // Tokenize-fail
        createChainMessageHandler([(msg) => msg.type === 'tokenize-fail'], () =>
            onMessage(createTokenizeFailMessage()),
        ),

        createChainMessageHandler(
            [
                (msg) =>
                    [
                        'form-init',
                        'init-checkout',
                        'form-ready',
                        'form-status',
                        'form-submit',
                        'auth-start',
                        'auth-end',
                        'auth-v2-start',
                        'auth-v2-end',
                        'challenge-v2-start',
                        'challenge-v2-end',
                        '3ds-status',
                        'sbp-bank-selected',
                        'sbp-start',
                        'trust-challenge-start',
                        'trust-challenge-end',
                        'otp-challenge-start',
                        'otp-challenge-end',
                        'otp-status',
                    ].includes(msg.type),
            ],
            (msg) => onMessage({ type: msg.type, data: msg.data } as FrameMessageV1),
        ),
    ];

    const handlers = ([] as Array<(message: PaymentMessage) => void>).concat(checkoutHandlers, oldFormHandlers);

    return (message) => handlers.forEach((handler) => handler(message));
}

export const listenPayment = (onMessage: FrameMessageListener): EmptyCb => {
    const handleFrameMessage = createFrameMessagesHandler(onMessage);
    const isDuplicate = getDuplicateMessageChecker();

    const handlePostMessage = (e: MessageEvent): void => {
        const message = getPaymentMessage(e.data);

        if (message && (checkIsEventCanBeDoubled(e) || !isDuplicate(message))) {
            handleFrameMessage(message);
        }
    };

    window.addEventListener('message', handlePostMessage);

    return (): void => {
        window.removeEventListener('message', handlePostMessage);
    };
};
